import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../store/store';
import { useSignOut } from '../../core/identity-access/store/signOut';
import { PortalAccountLinks, PortalNavList } from '../../design-system/chrome';
import { PortalEnvironment } from '../../design-system/environments';
import '../../design-system/core.generated.css';

/**
 * The portal shell every environment shares (docs/redesign/IQMaxx).
 *
 * Class names are the prototype's — `ushell`, `uhdr`, `uside`, `uscr` —
 * because the stylesheet is the prototype's, scoped per environment by
 * scripts/build-redesign-css.js. Renaming them here would mean re-deriving the
 * CSS by hand, which is how a port stops matching the thing it ported.
 *
 * One component, four environments. The header, the rail, the mobile drawer
 * and the account menu are identical in all of them; only the palette, the
 * badge and the nav differ, and those arrive as data. Three more shells would
 * be three more copies of this file, diverging the first time one is fixed.
 *
 * The screens rendered inside are the real ones. The shell is new; what it
 * frames is not a mock.
 */
interface Props {
  environment: PortalEnvironment;
}

const PortalShell: React.FC<Props> = ({ environment }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // PRT-17-FR-01 — the shared routine: revokes server-side, not just here.
  const signOut = useSignOut();
  const user = useSelector((state: RootState) => state.auth.user);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  // Close both on navigation: leaving a menu open across a route change is the
  // kind of thing that only shows up in a demo.
  useEffect(() => {
    setMenuOpen(false);
    setMobileNav(false);
  }, [location.pathname]);

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'IQ';

  const navItems = (
    <>
      {environment.cta && (
        <button className="cta" onClick={() => navigate(environment.cta!.to)}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span className="nlabel">{environment.cta.label}</span>
        </button>
      )}
      <PortalNavList environment={environment} onNavigate={() => setMobileNav(false)} />
    </>
  );

  return (
    <div className={environment.scope}>
      <div className="ushell">
        <div className="uhdr">
          <div className="uhl">
            <button className="umenu" onClick={() => setMobileNav((v) => !v)} aria-label="Menu">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <Link to={environment.home} className="ulogo">
              iQMaxx
            </Link>
            <span className="ubdg">{environment.badge}</span>
          </div>

          <div className="uhr">
            {environment.shortcut && (
              <button
                className="uhb"
                onClick={() => navigate(environment.shortcut!.to)}
                title={environment.shortcut.title}
              >
                {environment.shortcut.icon}
              </button>
            )}
            <button className="uhb" title="Notifications">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span className="undot" />
            </button>
            <button
              className="uhb"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="uav">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.9" strokeLinecap="round">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                </svg>
              </span>
              <span className="ulbl">{user?.firstName ?? 'Account'}</span>
            </button>

            <div className={`udd${menuOpen ? ' show' : ''}`} role="menu">
              <div className="uddh">
                <div className="n">{user ? `${user.firstName} ${user.lastName}` : initials}</div>
                <div className="e">{user?.email ?? 'Educator account'}</div>
              </div>
              <PortalAccountLinks environment={environment} itemClass="uddi" onSignOut={signOut} />
            </div>
          </div>
        </div>

        <div className="ubody">
          <nav className="uside">{navItems}</nav>

          {/* Mobile drawer — the prototype hides the rail under 768px. */}
          <div
            className={`umobov${mobileNav ? ' show' : ''}`}
            onClick={() => setMobileNav(false)}
            aria-hidden="true"
          />
          <nav className={`umob${mobileNav ? ' open' : ''}`}>{navItems}</nav>

          <div id="uContent">
            <div className="uscr" style={{ display: 'block' }}>
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalShell;
